import React, { useEffect, useState, useRef } from 'react'
import { apiDelete, apiDownloadFile, apiGet, apiPostFormData, apiPut, apiPutFormData } from '../../generic/Api_Functions'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { Button, IconButton, Stack, Typography } from '@mui/material'
import { AddCircleRounded, CancelRounded, CheckCircleRounded, InsertDriveFileRounded } from '@mui/icons-material'

import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueLabel, systemValueOptionText } from '../../generic/systemValues/systemValueLabel'

import AlertUI from '../../generic/AlertUI'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import SmartSelect from '../../generic/smartSelect/SmartSelect'
import CamParserSettings from './CamParserSettings'
import DownloadIcon from '@mui/icons-material/Download'
import { useAuthorization } from '../../generic/hooks/useAuthorization'

const Programs = () => {
  const [technologies, setTechnologies] = useState(null)
  const fileInputRef = useRef()
  const fileInputAddRef = useRef()

  const [AlertElement, showAlert] = AlertUI()

  const dataRow = useRef()
  const newRow = useRef()
  const [resetDataRow, setResetDataRow] = useState(false)
  const { t_ } = useSmartTranslation()
  const [dialogStatus, setDialogStatus] = useState({ open: false, file: null })
  const [controlOptions, setControlOptions] = useState([])
  const [controlSelectedName, setControlSelectedName] = useState('')
  const [controlSelectedId, setControlSelectedId] = useState([])
  const [openParserSettingDialog, setOpenParserSettingDialog] = useState(false)
  const [parserSettingTemp, setParserSettingTemp] = useState({ part: true, rawMaterial: true, fixture: false, tool: true })
  const [parserSetting, setParserSetting] = useState({ part: true, rawMaterial: true, fixture: false, tool: true })
  const isUserAuthorized = useAuthorization()

  const onRowClick = (row) => {
    newRow.current = row
    if (dataRow.current === null || dataRow.current === undefined) {
      dataRow.current = newRow.current
    } else if (dataRow.current['smart-table-index'] === newRow.current['smart-table-index'] || newRow.current['smart-table-index'] === -1) {
      dataRow.current = newRow.current
    }
  }

  useEffect(() => {
    if (resetDataRow) {
      dataRow.current = newRow.current
      setResetDataRow(false)
    }
  }, [resetDataRow, newRow])

  const columns = [
    { name: t_('Name'), field: 'name', tooltip: 'name', readOnly: true },
    {
      name: t_('Technology'),
      field: 'technology.name',
      tooltip: 'Technology',
      type: 'select',
      mandatory: true,
      // Story 204 — S78: by code, never by name (RN1.6); the value chosen is still the name.
      render: (row) => systemValueLabel(t_, 'technology', row.technology),
      renderOnlyInReadMode: true,
      edit: {
        source: async () => {
          return (technologies || null)
        },
        id: 'id',
        field: 'name',
        optionLabel: (name, items) => systemValueOptionText(t_, 'technology', items, name)
      }
    }
  ]

  useEffect(() => {
    const fetchData = async () => {
      const options = await apiGet('getControls/')
      setControlOptions(options)

      apiGet('technologiesWorkflow/noPagination').then((data) => {
        setTechnologies(data)
      })
    }
    fetchData()
    return () => {
    }
  }, [t_])

  const handleControlSelectChange = (newValue) => {
    setControlSelectedName(newValue)

    const selectedControl = controlOptions.find((opt) => opt.name === newValue)
    if (selectedControl) {
      setControlSelectedId(selectedControl.id)
    } else {
      setControlSelectedId(null)
    }
  }

  const finalConfigurationProgram = useRef({})
  const [technologySelected, setTechnologySelected] = useState()
  const noTechnologies = technologies !== null && technologies.length === 0

  function BodyModal (props) {
    return (
      <div sx={{ display: 'grid' }}>
        <Stack spacing={2}>
          <Typography component='span' variant='subtitle1'>{t_('1.- Assign a technology to this program')}</Typography>
          {technologies?.map((technology, index) =>
            <Button
              variant='contained'
              key={index}
              color={technologySelected ? technologySelected.name === technology.name ? 'primary' : 'inherit' : 'inherit'} // system-value: raw-ok
              sx={{ color: 'rgba(0, 0, 0, 0.87) !important' }}
              onClick={() => {
                setTechnologySelected(technology)
                finalConfigurationProgram.current = { technology }
              }}
            >
              {systemValueLabel(t_, 'technology', technology)}
            </Button>
          )}
          <SmartSelect
            label={t_('Control selection')}
            value={controlSelectedName}
            selectableOptions={controlOptions.map((opt) => opt.name)}
            onChange={handleControlSelectChange}
            disableClearable={false}
            autoSelectFirst={false}
          />
        </Stack>
      </div>
    )
  }

  const handleAcceptModal = async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    if (controlSelectedId) {
      formData.append('control', controlSelectedId)
    }

    if (!finalConfigurationProgram.current.technology) {
      showAlert({
        title: t_('Technology has not been choosen.'),
        severity: 'error'
      })
      return
    } else {
      formData.append('technology', finalConfigurationProgram.current.technology.id)
    }

    formData.append('part', parserSetting.part)
    formData.append('rawMaterial', parserSetting.rawMaterial)
    formData.append('fixture', parserSetting.fixture)
    formData.append('tool', parserSetting.tool)

    try {
      const response = await apiPostFormData('programs', formData)
      if (response) {
        showAlert({
          title: t_('Program saved correctly'),
          severity: 'success'

        })
      } else {
        const responseData = await response.json()

        showAlert({
          title: '',
          severity: 'error',
          message: responseData.message
        })
      }
      return response
    } catch (error) {
      showAlert({
        title: '',
        severity: 'error',
        message: error.message
      })
    }
  }

  const handleCreateRow = (event) => {
    setDialogStatus({ open: true, file: event.target.files[0] })
  }

  const handleChange = async (event) => {
    const formData = new FormData()
    formData.append('file', event.target.files[0])
    try {
      await apiPutFormData('programs/replace/' + row.current.id, formData)
      showAlert({
        title: t_('Program modified correctly'),
        severity: 'success'

      })
    } catch (error) {
      showAlert({
        title: t_('Could not replace program'),
        message: error.message,
        severity: 'error'
      })
    }
  }

  const row = useRef()

  const handleCreateClick = (event) => {
    setControlSelectedName(null)
    setControlSelectedId(null)
    event.target.value = null
  }

  const defaultToolbar = {
    renderSticky: {
      fetchFunction: () => {
        return (
          <>
            <Button
              disabled={!isUserAuthorized('ADD_PROGRAMS') || noTechnologies}
              sx={{ border: 0 }}
              color='primary'
              variant='outlined'
              onClick={() => {
                row.current = null
                fileInputAddRef.current.click()
              }}
            >
              <AddCircleRounded sx={{ fontSize: '1.2em' }} />
              <Typography sx={{ marginLeft: '0.4em', fontSize: '0.875rem' }}>{t_('Add program')}</Typography>
            </Button>
            <input
              onClick={(event) => handleCreateClick(event)}
              onChange={(event) => handleCreateRow(event)}
              multiple
              ref={fileInputAddRef}
              type='file'
              hidden
            />
          </>
        )
      }
    }
  }

  const actions = {
    delete: {
      fetchFunction: (row) => {
        return (apiDelete('programs/' + row[0].id))
      },
      disabled: !isUserAuthorized('DELETE_PROGRAMS')
    },

    edit: {
      fetchFunction: (row) => {
        return apiPut('programs/updateTechnology/' + row.id + '/' + row.technology.id)
      },
      disabled: !isUserAuthorized('EDIT_PROGRAMS')
    },
    custom: [
      /* {
        name: 'Upload Program',
        render: (upload) => {
          return (
            <IconButton onClick={() => handleUploadProgram(upload)}>
              <Publish />
            </IconButton>
          )
        }
      }, */
      {
        name: 'Download Program',
        render: (row) => {
          return (
            <IconButton disabled={!isUserAuthorized('DOWNLOAD_PROGRAMS')}>
              <DownloadIcon />
            </IconButton>
          )
        },
        onClick: async (row) => {
          try {
            await apiDownloadFile('program/download/' + row.id, row.name)
          } catch (error) {
            showAlert({
              title: t_('Could not find program'),
              severity: 'error'
            })
          }
        },
        disabled: !isUserAuthorized('DOWNLOAD_PROGRAMS')
      }
    ]
  }

  return (
    <div>
      <input
        id='changeProgram'
        onClick={(event) => {
          event.target.value = null
        }}
        onChange={(event) => handleChange(event)}
        multiple
        ref={fileInputRef}
        type='file'
        hidden
      />
      <SmartDialog
        cancel={false}
        accept={false}
        message={<BodyModal />}
        title={{ icon: <InsertDriveFileRounded color='primary' sx={{ fontSize: '1.8em' }} />, render: <Typography color='primary' variant='h8'>{dialogStatus.file?.name}</Typography> }}
        setOpen={dialogStatus.open}
        renderCustomButtons={
          <>
            <Button
              variant='outlined'
              onClick={() => setOpenParserSettingDialog(true)}
            >
              {t_('Settings')}
            </Button>
            <Button
              variant='outlined' onClick={() => setDialogStatus({ ...dialogStatus, open: false })} sx={{
                border: '1px solid rgba(255, 255, 255, 0.23) !important',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08) !important'
                }
              }}
            >
              <CancelRounded sx={{ color: 'lightgrey !important', fontSize: '1.4em', marginRight: '0.3rem' }} />
              <Typography sx={{ color: 'lightgrey' }}>{t_('CANCEL')}</Typography>
            </Button>
            <Button
              disabled={noTechnologies}
              onClick={() => {
                handleAcceptModal(dialogStatus.file)
                setDialogStatus({ ...dialogStatus, open: false })
              }}
              color='primary' variant='outlined'
            >
              <Typography> {t_('ACCEPT')} </Typography>
              <CheckCircleRounded color='primary' sx={{ fontSize: '1.4em', marginLeft: '0.3rem' }} />
            </Button>
          </>
        }
      />
      <SmartDialog
        setOpen={openParserSettingDialog}
        title={{ icon: <InsertDriveFileRounded color='primary' sx={{ fontSize: '1.8em' }} />, render: <Typography color='primary' variant='h8'>{t_('Parser Setting')}</Typography> }}
        cancelCallback={() => { setOpenParserSettingDialog(false); setParserSettingTemp(parserSetting) }}
        acceptCallback={() => { setOpenParserSettingDialog(false); setParserSetting(parserSettingTemp) }}
        message={
          <CamParserSettings
            parserSettings={parserSettingTemp}
            onChangeParserSettings={(newSettings) => setParserSettingTemp(newSettings)}
          />
        }
      />
      {AlertElement}

      {noTechnologies && (
        <Typography color='textSecondary' sx={{ mb: 2 }}>
          {t_('No technologies configured. Please add a technology first to upload programs.')}
        </Typography>
      )}

      <SmartMateriaUITable
        columns={columns}
        title={t_('Programs')}
        dataFetch='programs'
        actions={actions}
        dense
        fetchInterval={1}
        toolbar={defaultToolbar}
        onRowClick={(row) => onRowClick(row)}
        editingRow={setResetDataRow}
      />

    </div>
  )
}

export default Programs
